import React from "react";
import { useGetStrategiesQuery } from "../api";

interface InvestmentData {
    active: number;
    pending: number;
    withdraw_pending: number;
    transfer_pending: number;
}

interface InvestmentManagementProps {
    stats:
        | {
              investments: Record<string, InvestmentData>;
          }
        | null
        | undefined;
    dailyPercent: string;
    selectedStrategy: number;
    isAddingPercent: boolean;
    onDailyPercentChange: (value: string) => void;
    onStrategyChange: (strategy: number) => void;
    onAddPercent: () => void;
}

export const InvestmentManagement: React.FC<InvestmentManagementProps> = ({
    stats,
    dailyPercent,
    selectedStrategy,
    isAddingPercent,
    onDailyPercentChange,
    onStrategyChange,
    onAddPercent,
}) => {
    const { data: strategiesResponse } = useGetStrategiesQuery();
    const strategies = strategiesResponse?.data || [];

    const getStrategyName = (key: string) => {
        const id = parseInt(key.replace("strategy_", ""));
        const strategy = strategies.find(s => s.id === id);
        return strategy ? `${id}: ${strategy.name}` : `${id}: Стратегия ${id}`;
    };

    return (
        <div className="space-y-6">
            {/* Деньги инвестиций */}
            <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-4">
                <h2 className="text-xl font-semibold mb-2 text-gray-900 dark:text-white">
                    Деньги инвестиций
                </h2>
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                        <thead className="bg-gray-50 dark:bg-gray-900">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                                    Стратегия
                                </th>
                                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                                    Active
                                </th>
                                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                                    Pending
                                </th>
                                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                                    Withdraw Pending
                                </th>
                                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                                    Transfer Pending
                                </th>
                            </tr>
                        </thead>
                        <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                            {stats &&
                                Object.entries(stats.investments).map(
                                    ([key, value]) => (
                                        <tr key={key}>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                                                {getStrategyName(key)}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-900 dark:text-white">
                                                ${value.active.toFixed(2)}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-900 dark:text-white">
                                                ${value.pending.toFixed(2)}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-900 dark:text-white">
                                                $
                                                {value.withdraw_pending.toFixed(
                                                    2
                                                )}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-900 dark:text-white">
                                                $
                                                {value.transfer_pending.toFixed(
                                                    2
                                                )}
                                            </td>
                                        </tr>
                                    )
                                )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Дневной процент */}
            <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
                <h2 className="text-xl font-semibold mb-4 text-gray-900 dark:text-white">
                    Начислить дневной процент
                </h2>
                <div className="flex gap-4 items-end">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Процент
                        </label>
                        <input
                            type="number"
                            step="0.01"
                            value={dailyPercent}
                            onChange={(e) =>
                                onDailyPercentChange(e.target.value)
                            }
                            className="border border-gray-300 dark:border-gray-600 rounded-md px-4 py-2 w-32 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                            placeholder="0.00"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Стратегия
                        </label>
                        <select
                            value={selectedStrategy}
                            onChange={(e) =>
                                onStrategyChange(parseInt(e.target.value))
                            }
                            className="min-w-20 border border-gray-300 dark:border-gray-600 rounded-md px-4 pr-8 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        >
                            {strategies.map((s) => (
                                <option key={s.id} value={s.id}>
                                    {s.id}: {s.name}
                                </option>
                            ))}
                        </select>
                    </div>
                    <button
                        onClick={onAddPercent}
                        disabled={isAddingPercent}
                        className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-md disabled:opacity-50"
                    >
                        {isAddingPercent ? "Начисление..." : "Начислить"}
                    </button>
                </div>
            </div>
        </div>
    );
};
